import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../../components/Modal';
import { useScopedDictText } from '../../../utils/dictOptions';
import { BundleParamItem, MaterialBundlePreflight } from '../../../api/bundle';
import { listParams, ParamRow, RESULT_TAGS } from '../../../api/parameter';
import { ApiError } from '../../../api/request';
import SearchSelect from '../../../components/SearchSelect';

// 向导第 ④ 步的第三条路径: 从已有参数复制 (2026-09-16).
//
// 建一个新材料时, 绝大多数参数不是凭空想出来的, 而是照着一个相似材料的现成参数抄下来再微调
// (同材质换颜色、换厚度、换供应商都是这样). 只给"逐行手填"和"传 xlsx"两条路, 等于逼人
// 去参数页翻出旧行、把数字一个个抄过来.
//
// 复制的是**值**不是行: 选中的行按 (设备 / 加工类型 / 字段值 / 结果标) 复制成本材料的新行,
// 材料键由套件落库时注入 —— 一条参数行只能属于一个材料, 不存在"两个材料共用一行".
//
// 这里只读 param_row (POST /v1/params/list), 不碰设备与参数模板接口:
// 「耗材业务」角色对设备工况与参数模板都没有读权, 为了建材料去要那两个读权
// 是把别人的数据面塞进来。设备一列直接显示 deviceKey (它本来就是业务键), 够定位.
//
// 列表只给"这一行是哪个材料在哪台设备上做什么"这几个定位信息, 不铺参数值:
// 一行十几个字段值平铺出来, 挑行的时候反而看不清是哪一行; 要看具体数值点「详情」.

interface Props {
    preflight: MaterialBundlePreflight | null;
    onPick: (items: BundleParamItem[]) => void;
}

const PAGE_SIZE = 30;

/** parseParams 参数值 JSON → 对象 (坏数据不抛, 按空处理, 详情里照原样显示). */
function parseParams(raw?: string): Record<string, unknown> {
    if (!raw) return {};
    try {
        const v = JSON.parse(raw);
        return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
    } catch {
        return {};
    }
}

function CopyParamsPanel({ preflight, onPick }: Props) {
    // 参数挂类别不挂材料的框架 (喷墨) 不在候选里 —— 复制过来套件也会拒
    const archs = useMemo(
        () => (preflight?.archs || []).filter((a) => a.materialKeyKind !== 'category_code'),
        [preflight],
    );
    const [archCode, setArchCode] = useState('');
    const [kw, setKw] = useState('');
    const [rows, setRows] = useState<ParamRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [picked, setPicked] = useState<Set<number>>(new Set());
    const [detail, setDetail] = useState<ParamRow | null>(null);
    const processText = useScopedDictText('process_type', [archCode]);
    // 翻页防重入: 滚动事件密集, 上一页没回来不发下一页
    const loadingRef = useRef(false);

    useEffect(() => {
        if (!archCode && archs.length) setArchCode(archs[0].code);
    }, [archs, archCode]);

    const fetchPage = useCallback(
        async (p: number, append: boolean) => {
            if (!archCode || loadingRef.current) return;
            loadingRef.current = true;
            setLoading(true);
            setErr('');
            try {
                const res = await listParams(archCode, {
                    keyword: kw || undefined,
                    // 只复制已发布的行: draft/reviewing 还没定稿, 抄过来等于把未定的数放进新材料
                    status: 'published',
                    page: p,
                    pageSize: PAGE_SIZE,
                    lite: true,
                });
                const list = res.list || [];
                setRows((cur) => (append ? [...cur, ...list] : list));
                setTotal(res.totalCount || 0);
                setPage(p);
            } catch (e) {
                setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        },
        [archCode, kw],
    );

    const search = () => {
        setPicked(new Set());
        fetchPage(1, false);
    };

    // 切框架就重搜: 参数表按框架分表, 换了框架旧结果没有意义
    useEffect(() => {
        if (!archCode) return;
        setPicked(new Set());
        fetchPage(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archCode]);

    // 滚到底继续加载 (总数可能上千行, 一次拉全会把列表拖垮)
    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight > 80) return;
        if (loadingRef.current || rows.length >= total) return;
        fetchPage(page + 1, true);
    };

    const toggle = (id: number) => {
        const next = new Set(picked);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setPicked(next);
    };

    const copy = () => {
        const items: BundleParamItem[] = rows
            .filter((r) => picked.has(r.id))
            .map((r) => {
                const item: BundleParamItem = {
                    archCode,
                    deviceKey: r.deviceKey,
                    subProcess: r.subProcess || '',
                    params: parseParams(r.params),
                    resultTag: r.resultTag,
                    remark: r.remark || '',
                };
                // 双材料键框架: 源行的另一半照搬 (本材料补源行里"本材料那一侧")
                if (r.substrateXcode) item.substrateXcode = r.substrateXcode;
                return item;
            });
        onPick(items);
        setPicked(new Set());
    };

    return (
        <div className="card p-4 space-y-3">
            <div className="text-sm font-semibold">从已有参数复制</div>
            <div className="flex flex-wrap items-end gap-2">
                <div className="w-40">
                    <label className="field-label">工艺框架</label>
                    <SearchSelect
                        className="w-full"
                        value={archCode}
                        onChange={(v) => setArchCode(v)}
                        options={archs.map((a) => ({ value: a.code, label: a.label }))}
                    />
                </div>
                <div className="flex-1 min-w-[14rem]">
                    <label className="field-label">材料编码 / 设备型号 关键词</label>
                    <input
                        className="inp"
                        value={kw}
                        onChange={(e) => setKw(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && search()}
                        placeholder="例: PL003 / MD3-blue-20W"
                    />
                </div>
                <button type="button" className="btn-ghost" onClick={search} disabled={loading}>
                    {loading ? '搜索中…' : '搜索'}
                </button>
            </div>
            {err && <div className="text-xs text-rose-600">{err}</div>}

            {rows.length === 0 ? (
                <div className="text-xs text-slate-400">
                    {loading ? '加载中…' : '没有匹配的已发布参数行。'}
                </div>
            ) : (
                <>
                    <div className="text-[11px] text-slate-400">
                        已加载 {rows.length} / {total} 行 · 只列已发布的行 · 下滑继续加载
                    </div>
                    <div
                        className="max-h-80 overflow-auto border border-slate-100 rounded"
                        onScroll={onScroll}
                    >
                        <table className="w-full">
                            <thead className="sticky top-0 bg-white z-[1]">
                                <tr>
                                    <th className="th w-8" />
                                    <th className="th">来源材料</th>
                                    <th className="th">设备</th>
                                    <th className="th">加工类型</th>
                                    <th className="th">结果</th>
                                    <th className="th w-14" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="cursor-pointer hover:bg-slate-50"
                                        onClick={() => toggle(r.id)}
                                    >
                                        <td className="td">
                                            <input
                                                type="checkbox"
                                                checked={picked.has(r.id)}
                                                onChange={() => toggle(r.id)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td className="td font-mono text-xs">
                                            {r.xcode || r.filmXcode || '—'}
                                        </td>
                                        <td className="td text-xs font-mono">{r.deviceKey}</td>
                                        <td className="td text-xs">
                                            {processText(archCode, r.subProcess)}
                                        </td>
                                        <td className="td text-xs">
                                            {RESULT_TAGS.find((t) => t.v === r.resultTag)?.label ||
                                                r.resultTag}
                                        </td>
                                        <td className="td">
                                            <button
                                                type="button"
                                                className="text-xs text-brand-600 hover:underline"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDetail(r);
                                                }}
                                            >
                                                详情
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {loading && rows.length > 0 && (
                            <div className="text-center text-[11px] text-slate-400 py-2">
                                加载中…
                            </div>
                        )}
                        {!loading && rows.length >= total && total > 0 && (
                            <div className="text-center text-[11px] text-slate-300 py-2">
                                已到底
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-400">
                            按原样挂到本材料: 设备 / 加工类型 / 字段值 / 结果标照搬, 只把材料换成本材料。
                        </span>
                        <button
                            type="button"
                            className="ml-auto btn-primary"
                            disabled={picked.size === 0}
                            onClick={copy}
                        >
                            挂 {picked.size} 行到本材料
                        </button>
                    </div>
                </>
            )}

            <ParamRowDetailModal
                row={detail}
                archCode={archCode}
                processText={processText}
                onClose={() => setDetail(null)}
                onPick={(id) => {
                    toggle(id);
                    setDetail(null);
                }}
                picked={detail ? picked.has(detail.id) : false}
            />
        </div>
    );
}

/** ParamRowDetailModal 一行的完整内容: 定位信息 + 全部字段值, 挑行时按需看. */
function ParamRowDetailModal({
    row,
    archCode,
    processText,
    onClose,
    onPick,
    picked,
}: {
    row: ParamRow | null;
    archCode: string;
    processText: (scope: string | undefined, code?: string, fallback?: string) => string;
    onClose: () => void;
    onPick: (id: number) => void;
    picked: boolean;
}) {
    const params = useMemo(() => parseParams(row?.params), [row]);
    if (!row) return null;
    return (
        <Modal open title="参数行详情" width={640} onClose={onClose}>
            <div className="space-y-3">
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                    <span className="text-slate-400">来源材料</span>
                    <span className="font-mono">
                        {row.xcode || row.filmXcode || '—'}
                        {row.substrateXcode && (
                            <span className="text-slate-400"> · 基材 {row.substrateXcode}</span>
                        )}
                    </span>
                    <span className="text-slate-400">设备</span>
                    <span className="font-mono">{row.deviceKey}</span>
                    <span className="text-slate-400">加工类型</span>
                    <span>{processText(archCode, row.subProcess)}</span>
                    <span className="text-slate-400">检查结果</span>
                    <span>
                        {RESULT_TAGS.find((t) => t.v === row.resultTag)?.label || row.resultTag}
                    </span>
                    {row.remark && (
                        <>
                            <span className="text-slate-400">备注</span>
                            <span className="break-all">{row.remark}</span>
                        </>
                    )}
                </div>

                <div>
                    <div className="text-xs font-medium text-slate-600 mb-1">
                        参数值 ({Object.keys(params).length})
                    </div>
                    {Object.keys(params).length === 0 ? (
                        <div className="text-xs text-slate-400">该行没有参数值。</div>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {Object.entries(params).map(([k, v]) => (
                                <div
                                    key={k}
                                    className="flex justify-between gap-2 text-xs border-b border-slate-50 py-1"
                                >
                                    <span className="text-slate-400 font-mono">{k}</span>
                                    <span className="text-slate-700 font-mono text-right break-all">
                                        {v == null ? '—' : String(v)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" className="btn-ghost" onClick={onClose}>
                        关闭
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => onPick(row.id)}
                    >
                        {picked ? '取消选择' : '选择这一行'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default CopyParamsPanel;
